import apiEndPoints from "../../apiconstants";
import { api, deafultResponseAPIResponse, deafultResponseDatArrayAPIResponse, handleErrorResponse } from "../api";
import { Advisor, AdvisorAssetLogoRequestPayload, createAdvisorResponse, DeleteAdvisorResponse, GetAdvisorEnquiryPrompts, GetAdvisorPrompts, GetAdvisorsAPIResponse, GetInviteUsersForAdvisorResponse, InvitePalAPIResponse, InvitePalPayload, UpdateAdvisorStatusResponse } from "./advisortypes";


// Define a service using a base URL and expected endpoints
export const advisorApiSlice = api.injectEndpoints({
  endpoints: builder => ({
    getAdvisors: builder.query<GetAdvisorsAPIResponse, boolean | undefined>({
      query: (isPalView = false) => isPalView ? apiEndPoints?.getPalAdvisors : apiEndPoints?.getAdvisors,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, id) => [{ type: "Advisor", id: String(id) }],
    }),
    getAdvisorById: builder.query<GetAdvisorsAPIResponse, string>({
      query: (advisor_id) => `${apiEndPoints?.getAdvisors}/${advisor_id}`,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, id) => [{ type: "Advisor", id: String(id) }],
    }),
    updateAdvisor: builder.mutation<deafultResponseAPIResponse, Partial<Advisor>>({
      query: (advisorData) => ({
        url: apiEndPoints?.updateAdvisor,
        method: 'PATCH',
        body: advisorData
      }),
      invalidatesTags: (result, error, advisorData) => [{ type: 'Advisor', id: String(advisorData.advisor_id) }],
      transformErrorResponse: handleErrorResponse,
    }),
    uploadAdvisorLogo: builder.mutation<deafultResponseDatArrayAPIResponse, AdvisorAssetLogoRequestPayload>({
      query: (logoRequestPayload) => ({
        url: apiEndPoints?.uploadAdvisorLogo,
        method: 'POST',
        body: logoRequestPayload
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, advisorData) => [{ type: 'Advisor', id: String(advisorData.advisor_id) }],
    }),
    createAdvisor: builder.mutation<createAdvisorResponse, Partial<Advisor>>({
      query: (advisorData) => ({
        url: apiEndPoints?.createAdvisor, // Adjust URL if necessary
        method: 'POST',
        body: advisorData,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    updateAdvisorStatus: builder.mutation<UpdateAdvisorStatusResponse, Partial<Advisor>>({
      query: ({ advisor_id, status }) => ({
        url: apiEndPoints?.updateAdvisorStatus,
        method: 'POST',
        body: {
          advisor_id,
          status
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, advisorData) => [{ type: 'Advisor', id: String(advisorData.advisor_id) }],
    }),
    deleteAdvisor: builder.mutation<DeleteAdvisorResponse, string>({
      query: (advisor_id) => ({
        url: `${apiEndPoints?.deleteAdvisor}${advisor_id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    invitePalUser: builder.mutation<InvitePalAPIResponse, InvitePalPayload>({
      query: (invitePalPayload) => ({
        url: apiEndPoints?.invitePalUser,
        method: 'POST',
        body: invitePalPayload
      }),
      transformErrorResponse: handleErrorResponse
    }),
    getPalUserDetails: builder.query<GetInviteUsersForAdvisorResponse, string>({
      query: (advisor_id) => `${apiEndPoints?.getPalUserDetails}?advisor_id=${advisor_id}`,
      transformErrorResponse: handleErrorResponse,
    }),
    updateDefaultAdvisor: builder.mutation<deafultResponseAPIResponse, { advisorID: string, isDefault: boolean; }>({
      query: (payload) => ({
        url: apiEndPoints?.defaultAdvisor,
        method: 'POST',
        body: {
          advisor_id: payload.advisorID,
          is_default: payload.isDefault
        }
      }),
      transformErrorResponse: handleErrorResponse
    }),
    getAdvisorPrompts: builder.query<GetAdvisorPrompts, { advisorId: string; }>({
      query: ({ advisorId }) => `${apiEndPoints?.getadvisorprompts}/qa/${advisorId}`,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, id) => [{ type: "Advisor", id: String(id) }],
    }),
    getAdvisorEnquiryPrompts: builder.query<GetAdvisorEnquiryPrompts, { advisorId: string; }>({
      query: ({ advisorId }) => `${apiEndPoints?.getadvisorprompts}/enquirybuilder/${advisorId}`,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, id) => [{ type: "Advisor", id: String(id) }],
    }),
    updateAdvisorPrompts: builder.mutation<deafultResponseAPIResponse, { advisorId: string, type: string, prompt: string; }>({
      query: ({ advisorId, type, prompt }) => ({
        url: `${apiEndPoints?.updateAdvisorPrompts}/${advisorId}`,
        method: 'POST',
        body: {
          type: type,
          prompt: prompt
        }
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getAdvisorVersions: builder.mutation<any, { advisor_id: string, page?: number, per_page?: number }>({
      query: ({ advisor_id, page = 1, per_page = 500 }) => ({
        url: `${apiEndPoints?.configurationManagement}/versions`,
        method: 'POST',
        body: {
          scope: 'tenant',
          aiworker_id: advisor_id,
          config_name: 'advisor_config',
          pagination: { page, per_page },
        },
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response?.data,
    }),
    fetchSingleVersion: builder.mutation<any, { advisor_id: string, version_id: string }>({
      query: ({ advisor_id, version_id }) => ({
        url: `${apiEndPoints?.advancedSettings}?advisor_id=${advisor_id}&version_id=${version_id}`,
        method: 'GET'
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response?.data,
    }),
    updateAdvisorVersion: builder.mutation<any, { advisor_id: string, fields: any }>({
      query: (payload) => ({
        url: `${apiEndPoints?.advancedSettings}`,
        method: 'POST',
        body: payload
      }),
    }),
    rollbackAdvisorVersion: builder.mutation<any, { config_id: string, version_id: string, config_name?: string }>({
      query: ({ config_id, version_id, config_name = 'advisor_config' }) => ({
        url: `${apiEndPoints?.configurationManagement}/rollback`,
        method: 'PATCH',
        body: {
          scope: 'tenant',
          config_id,
          config_name,
          version_id,
        }
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    updateAdvisorVersionTag: builder.mutation<any, { advisor_id: string, version_id: string, tags?: string[], version_name?: string }>({
      query: (payload) => ({
        url: `${apiEndPoints?.advancedSettings}/tags`,
        method: 'PATCH',
        body: payload
      }),
      transformErrorResponse: handleErrorResponse,
    }),
  })
});

// Hooks are auto-generated by RTK-Query
// Same as `advisorApiSlice.endpoints.getQuotes.useQuery`
export const { useGetAdvisorsQuery, useGetAdvisorByIdQuery, useUpdateAdvisorMutation,
  useUploadAdvisorLogoMutation, useCreateAdvisorMutation, useUpdateAdvisorStatusMutation, useDeleteAdvisorMutation, useInvitePalUserMutation, useGetPalUserDetailsQuery, useUpdateDefaultAdvisorMutation, useGetAdvisorPromptsQuery,
  useGetAdvisorEnquiryPromptsQuery,
  useUpdateAdvisorPromptsMutation,
  useGetAdvisorVersionsMutation,
  useFetchSingleVersionMutation,
  useUpdateAdvisorVersionMutation,
  useRollbackAdvisorVersionMutation,
  useUpdateAdvisorVersionTagMutation,
} = advisorApiSlice;